/**
 * Una richiesta di body rental sul telefono (7/10/2026): risponde a "a che
 * punto è, e qual è il prossimo passo?".
 *
 * Sul computer è una tabella con dieci colonne di tendine e campi da
 * scrivere dentro le celle: a 390px usciva dallo schermo. Qui:
 *   1. il titolo, il cliente e lo stato (si cambia toccando la riga, come gli
 *      step del computer);
 *   2. il prossimo passo, lo stesso che suggerisce il computer, con il suo
 *      tasto (aggiungi una figura, genera la fattura, segna pagato, termina);
 *   3. le figure, una riga ciascuna: chi è, a che tariffa, se è fatturata e
 *      pagata. Toccandola si apre la sua pagina, dove ogni valore è una riga
 *      che si tocca (MScelte o MCampo): niente moduli in fila;
 *   4. i soldi e le note.
 * Annullare ed eliminare stanno nel menu ⋯ in alto. Stesse chiamate e stessa
 * cache del computer.
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur } from '../../lib/utils';
import { Icon, type IconName } from '../../components/ui/Icon';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { PageActions } from '../../components/layout/PageActions';
import { MCampo, MEmpty, MGroup, MHead, MPage, MRow, MScelte, leggibile } from '../../components/mobile/MobileKit';
import { BodyRentalStatus } from '@masamune/shared';
import { FASE_BR } from './TalentMobile';
import './talent-mobile.css';

interface BREngagement {
  id: string; role: string; seniority: string | null; supplierId: string | null;
  supplier: { id: string; fullName: string; email: string | null; phone: string | null; city: string | null } | null;
  status: string; rateUnit: string; clientRate: number; supplierCost: number; quantity: number;
  startDate: string | null; endDate: string | null; supplierPaidAt: string | null; notes: string | null;
  invoices: Array<{ id: string; number: string | null; status: string; total: string; issueDate: string | null; paidDate: string | null }>;
  revenue: number; cost?: number; margin?: number;
}
interface BRDetail {
  id: string; title: string; description: string | null; status: string; neededRoles: number;
  client: { id: string; legalName: string; shortName: string };
  project: { id: string; name: string; code: string } | null;
  engagements: BREngagement[];
  totals: { revenue: number; cost?: number; margin?: number };
}

const UNITA: Record<string, string> = { HOUR: "all'ora", DAY: 'al giorno', MONTH: 'al mese' };
const STATO: Record<string, string> = {
  RICHIESTA: 'Richiesta', RICERCA: 'Cerchiamo la figura', PROPOSTO: 'Proposta al cliente', ATTIVO: 'Ingaggio in corso',
  TERMINATO: 'Terminata', ANNULLATO: 'Annullata',
};
const giorno = (iso: string) => new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '');

type Pannello = null | 'stato' | 'nuova' | 'note' | 'pro' | 'tariffa' | 'unita' | 'costo' | 'quantita' | 'statoFigura';

export function BodyRentalMobile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const [figuraId, setFiguraId] = useUrlState<string>('figura', '');
  const [aperto, setAperto] = useState<Pannello>(null);
  const [bozza, setBozza] = useState('');

  const { data: r, isLoading, isError } = useQuery({
    queryKey: ['body-rental', id],
    queryFn: () => api.get<BRDetail>(`/body-rental/${id}`),
    enabled: !!id,
    retry: false,
  });
  // La stessa chiave del computer per l'elenco dei professionisti.
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; fullName: string }> }>('/suppliers?pageSize=200'),
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['body-rental', id] }); qc.invalidateQueries({ queryKey: ['body-rental', 'list'] }); };
  const errore = (e: unknown) => toast.alert('Non salvato', e instanceof ApiError ? e.message : 'Riprova');
  const patchRequest = useMutation({ mutationFn: (body: any) => api.patch(`/body-rental/${id}`, body), onSuccess: invalidate, onError: errore });
  const patchEng = useMutation({
    mutationFn: ({ engId, body }: { engId: string; body: any }) => api.patch(`/body-rental/engagements/${engId}`, body),
    onSuccess: invalidate,
    onError: errore,
  });
  const addEng = useMutation({
    mutationFn: (body: any) => api.post(`/body-rental/${id}/engagements`, body),
    onSuccess: () => { invalidate(); toast.ok('Figura aggiunta', 'Tocca la riga per tariffa e professionista.'); },
    onError: errore,
  });
  const delEng = useMutation({ mutationFn: (engId: string) => api.delete(`/body-rental/engagements/${engId}`), onSuccess: invalidate });
  const genInvoice = useMutation({
    mutationFn: (engId: string) => api.post<{ id: string }>(`/body-rental/engagements/${engId}/invoice`, {}),
    onSuccess: (inv) => { invalidate(); toast.ok('Fattura creata', 'Bozza pronta da emettere'); navigate(`/invoices/${inv.id}`); },
    onError: errore,
  });

  if (isError) return <MPage><MEmpty>Richiesta non trovata: forse è stata eliminata, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !r) return <MPage><MEmpty>Carico la richiesta…</MEmpty></MPage>;

  const annullata = r.status === BodyRentalStatus.ANNULLATO;
  const assegnate = r.engagements.filter((e) => e.supplierId).length;
  const chiudi = () => setAperto(null);
  const apri = (p: Pannello, v = '') => { setBozza(v); setAperto(p); };

  // Eliminazione come sul computer: sparisce subito, con "Annulla" nel messaggio.
  const elimina = () => {
    const titolo = r.title;
    qc.setQueryData<{ items: Array<{ id: string }> }>(['body-rental', 'list'], (old) => (old ? { ...old, items: old.items.filter((i) => i.id !== id) } : old));
    navigate('/talent?p=bodyrental');
    api.delete(`/body-rental/${id}`)
      .then(() => qc.invalidateQueries({ queryKey: ['body-rental'] }))
      .catch(() => { toast.alert('Eliminazione non riuscita', 'Riprova'); qc.invalidateQueries({ queryKey: ['body-rental'] }); });
    toast.undo('Richiesta eliminata', {
      description: titolo,
      onUndo: () => api.post(`/body-rental/${id}/restore`).then(() => qc.invalidateQueries({ queryKey: ['body-rental'] })).catch(() => toast.alert('Ripristino non riuscito', 'Riprova')),
    });
  };

  // Il prossimo passo: lo stesso ragionamento della scheda del computer.
  const passo: { titolo: string; sub: string; icon: IconName; azione: () => void } | null = (() => {
    if (annullata) return null;
    if (!r.engagements.length) return { titolo: 'Definisci le figure', sub: `Aggiungi i professionisti richiesti (${r.neededRoles} previsti).`, icon: 'plus', azione: () => apri('nuova') };
    if (r.engagements.some((e) => !e.supplierId)) return { titolo: 'Assegna i professionisti', sub: `${r.engagements.length - assegnate} figure ancora senza professionista: tocca la figura.`, icon: 'users', azione: () => setFiguraId(r.engagements.find((e) => !e.supplierId)!.id) };
    const daFatturare = r.engagements.find((e) => e.supplierId && e.invoices.length === 0);
    if (daFatturare) return { titolo: 'Genera la fattura', sub: `Tutte le figure sono assegnate: fattura "${daFatturare.role}" al cliente.`, icon: 'invoice', azione: () => genInvoice.mutate(daFatturare.id) };
    const daPagare = r.engagements.find((e) => e.supplierId && !e.supplierPaidAt);
    if (daPagare) return { titolo: 'Paga i professionisti', sub: `Segna il pagamento a ${daPagare.supplier?.fullName ?? daPagare.role}.`, icon: 'euro', azione: () => patchEng.mutate({ engId: daPagare.id, body: { supplierPaid: true } }) };
    if (r.status !== BodyRentalStatus.TERMINATO) return { titolo: 'Chiudi la richiesta', sub: 'Figure fatturate e pagate: segnala come terminata.', icon: 'check', azione: () => patchRequest.mutate({ status: BodyRentalStatus.TERMINATO }) };
    return null;
  })();

  const figura = figuraId ? r.engagements.find((e) => e.id === figuraId) ?? null : null;
  const numero = (k: 'clientRate' | 'supplierCost' | 'quantity', titolo: string, nota?: string): ReactNode => (
    <MCampo
      open={aperto === (k === 'clientRate' ? 'tariffa' : k === 'supplierCost' ? 'costo' : 'quantita')}
      onClose={chiudi}
      titolo={titolo}
      nota={nota}
      campo={<input className="inp mono" type="number" inputMode="decimal" autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label={titolo} />}
      onSalva={() => { const v = Number(bozza); if (figura && Number.isFinite(v)) patchEng.mutate({ engId: figura.id, body: { [k]: v } }); chiudi(); }}
    />
  );

  return (
    <>
      <PageActions className="flex gap-2">
        <ActionsMenu
          variant="ghost"
          label="Azioni"
          items={[
            { key: 'cancel', label: 'Annulla la richiesta', icon: 'x', hidden: annullata || r.status === BodyRentalStatus.TERMINATO, onClick: () => patchRequest.mutate({ status: BodyRentalStatus.ANNULLATO }) },
            { key: 'delete', label: 'Elimina', icon: 'trash', danger: true, onClick: elimina },
          ]}
        />
      </PageActions>

      <div className="tlm">
        {figura ? (
          <MPage>
            <button type="button" className="tlm-azione tlm-indietro" onClick={() => setFiguraId('')}>
              <Icon name="chevronL" size={16} /> {r.title}
            </button>
            <MHead title={figura.role} sub={[figura.seniority, STATO[figura.status] ?? figura.status].filter(Boolean).join(' · ')} />
            <MGroup title="Chi e a che prezzo">
              <MRow title="Professionista" trail={figura.supplier?.fullName ?? 'da assegnare'} trailTone={figura.supplier ? 'mute' : 'alert'} onClick={() => apri('pro')} />
              <MRow title="Tariffa al cliente" trail={`€ ${fmtEur(figura.clientRate)} ${UNITA[figura.rateUnit] ?? ''}`} trailTone="mute" onClick={() => apri('tariffa', String(figura.clientRate))} />
              <MRow title="Unità della tariffa" trail={UNITA[figura.rateUnit] ?? figura.rateUnit} trailTone="mute" onClick={() => apri('unita')} />
              {showMargin && <MRow title="Costo del professionista" trail={`€ ${fmtEur(figura.supplierCost)}`} trailTone="mute" onClick={() => apri('costo', String(figura.supplierCost))} />}
              <MRow title="Quantità" trail={String(figura.quantity)} trailTone="mute" onClick={() => apri('quantita', String(figura.quantity))} />
              <MRow title="Stato" trail={STATO[figura.status] ?? figura.status} trailTone="mute" onClick={() => apri('statoFigura')} />
            </MGroup>
            <MGroup title="Soldi">
              <MRow title="Ricavo" trail={`€ ${fmtEur(figura.revenue)}`} />
              {showMargin && <MRow title="Margine" trail={`€ ${fmtEur(figura.margin ?? 0)}`} />}
              {figura.invoices[0] ? (
                <MRow icon="invoice" title={`Fattura ${figura.invoices[0].number ?? 'in bozza'}`} sub={figura.invoices[0].status.replace(/_/g, ' ').toLowerCase()} onClick={() => navigate(`/invoices/${figura.invoices[0]!.id}`)} />
              ) : (
                <button type="button" className="tlm-azione" disabled={!figura.supplierId || genInvoice.isPending} onClick={() => genInvoice.mutate(figura.id)}>
                  <Icon name="invoice" size={15} /> {figura.supplierId ? 'Genera la fattura al cliente' : 'La fattura si genera dopo aver assegnato il professionista'}
                </button>
              )}
              <button type="button" className="tlm-azione" disabled={!figura.supplierId || patchEng.isPending} onClick={() => patchEng.mutate({ engId: figura.id, body: { supplierPaid: !figura.supplierPaidAt } })}>
                <Icon name="check" size={15} /> {figura.supplierPaidAt ? `Professionista pagato il ${giorno(figura.supplierPaidAt)}: annulla` : 'Segna il professionista come pagato'}
              </button>
            </MGroup>
            <MGroup title="Figura">
              <button
                type="button"
                className="tlm-azione is-danger"
                onClick={async () => {
                  if (await confirmDialog({ title: 'Rimuovi figura', body: `Rimuovere "${figura.role}"?`, danger: true, confirmLabel: 'Rimuovi' })) { delEng.mutate(figura.id); setFiguraId(''); }
                }}
              >
                <Icon name="trash" size={15} /> Rimuovi la figura
              </button>
            </MGroup>
          </MPage>
        ) : (
          <MPage>
            <MHead title={r.title} sub={[leggibile(r.client.shortName || r.client.legalName), r.project ? r.project.name : null].filter(Boolean).join(' · ')} />

            <MGroup title="A che punto è">
              <MRow icon="brief" tone={annullata ? 'alert' : undefined} title="Stato" trail={STATO[r.status] ?? r.status} trailTone="mute" onClick={() => apri('stato')} />
              <MRow icon="users" title="Figure" sub={`${r.neededRoles} richieste`} trail={`${assegnate} su ${r.engagements.length}`} trailTone="mute" />
              {passo && <MRow icon={passo.icon} tone="ink" title={passo.titolo} sub={passo.sub} subWrap onClick={passo.azione} />}
            </MGroup>

            <MGroup title="Figure ingaggiate" count={r.engagements.length || undefined}>
              {r.engagements.map((e) => (
                <MRow
                  key={e.id}
                  icon="user"
                  tone={!e.supplierId ? 'alert' : undefined}
                  title={e.role}
                  sub={[
                    e.supplier?.fullName ?? 'professionista da assegnare',
                    `€ ${fmtEur(e.clientRate)} ${UNITA[e.rateUnit] ?? ''} × ${e.quantity}`,
                    e.invoices.length ? 'fatturata' : null,
                    e.supplierPaidAt ? 'pagata' : null,
                  ].filter(Boolean).join(' · ')}
                  onClick={() => setFiguraId(e.id)}
                />
              ))}
              {!r.engagements.length && <MEmpty>Nessuna figura: aggiungi i professionisti richiesti.</MEmpty>}
              <button type="button" className="tlm-azione" onClick={() => apri('nuova')}><Icon name="plus" size={15} /> Aggiungi una figura</button>
            </MGroup>

            <MGroup title="Soldi">
              <MRow icon="euro" title="Ricavo dal cliente" trail={`€ ${fmtEur(r.totals.revenue)}`} />
              {showMargin && <MRow icon="receipt" title="Costo dei professionisti" trail={`€ ${fmtEur(r.totals.cost ?? 0)}`} />}
              {showMargin && <MRow icon="percent" title="Margine" sub={r.totals.revenue > 0 ? `${(((r.totals.margin ?? 0) / r.totals.revenue) * 100).toFixed(0)}% del ricavo` : undefined} trail={`€ ${fmtEur(r.totals.margin ?? 0)}`} />}
            </MGroup>

            {r.description && (
              <MGroup title="Note">
                <MRow icon="pencil" title="Note della richiesta" sub={r.description.split('\n')[0]} onClick={() => apri('note')} />
              </MGroup>
            )}
          </MPage>
        )}
      </div>

      <MScelte
        open={aperto === 'stato'}
        onClose={chiudi}
        titolo="Stato della richiesta"
        opzioni={FASE_BR.map(([k]) => ({ v: k, l: STATO[k] ?? k }))}
        valore={r.status}
        onScegli={(v) => { chiudi(); if (v !== r.status) patchRequest.mutate({ status: v }); }}
      />
      <MCampo
        open={aperto === 'nuova'}
        onClose={chiudi}
        titolo="Nuova figura"
        nota="Il ruolo, per esempio Sviluppatore React. Tariffa e professionista si aggiungono dopo, toccando la figura."
        campo={<input className="inp" autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label="Ruolo della figura" />}
        onSalva={() => {
          const role = bozza.trim();
          if (!role) return;
          addEng.mutate({ role, seniority: '', rateUnit: 'DAY', clientRate: 0, supplierCost: 0, quantity: 1 });
          chiudi();
        }}
      />
      <MobileSheet open={aperto === 'note'} onClose={chiudi} title="Note della richiesta">
        <div className="tlm-testo">{r.description}</div>
      </MobileSheet>

      {figura && (
        <>
          <MScelte
            open={aperto === 'pro'}
            onClose={chiudi}
            titolo="Professionista"
            opzioni={[{ v: '', l: 'Da assegnare' }, ...(suppliers?.items ?? []).map((s) => ({ v: s.id, l: leggibile(s.fullName) }))]}
            valore={figura.supplierId ?? ''}
            onScegli={(v) => {
              chiudi();
              // Come sul computer: assegnando qualcuno a una figura "in ricerca" la si propone al cliente.
              patchEng.mutate({ engId: figura.id, body: { supplierId: v || null, ...(v ? { status: figura.status === 'RICERCA' ? 'PROPOSTO' : figura.status } : {}) } });
            }}
          />
          <MScelte
            open={aperto === 'unita'}
            onClose={chiudi}
            titolo="Unità della tariffa"
            opzioni={Object.entries(UNITA).map(([v, l]) => ({ v, l: l.charAt(0).toUpperCase() + l.slice(1) }))}
            valore={figura.rateUnit}
            onScegli={(v) => { chiudi(); if (v !== figura.rateUnit) patchEng.mutate({ engId: figura.id, body: { rateUnit: v } }); }}
          />
          <MScelte
            open={aperto === 'statoFigura'}
            onClose={chiudi}
            titolo="Stato della figura"
            opzioni={FASE_BR.map(([k]) => ({ v: k, l: STATO[k] ?? k }))}
            valore={figura.status}
            onScegli={(v) => { chiudi(); if (v !== figura.status) patchEng.mutate({ engId: figura.id, body: { status: v } }); }}
          />
          {numero('clientRate', 'Tariffa al cliente', `In euro ${UNITA[figura.rateUnit] ?? ''}.`)}
          {numero('supplierCost', 'Costo del professionista', 'In euro, quello che paghiamo noi.')}
          {numero('quantity', 'Quantità', `Quante unità (${UNITA[figura.rateUnit]?.replace(/^al(l')? ?/, '') ?? 'unità'}) si fatturano.`)}
        </>
      )}
    </>
  );
}
